<template>
  <div class="robofinder-questionnaire" :class="{ 'is-cover': currentStep === 0 }">
    <div class="questionnaire-card" :class="{ 'is-cover': currentStep === 0 }">
      <!-- Results header -->
      <div class="results-header" v-if="resultCount !== null && currentStep > 0">
        <h2 class="results-count">{{ resultCount }} {{ resultCountLabel }}</h2>
        <div class="progress-dots">
          <span
            v-for="n in totalSteps"
            :key="n"
            class="progress-dot"
            :class="{ 'is-active': n <= currentStep }"
          ></span>
        </div>
        <button class="header-close-button" @click="handleClose">
          ×
        </button>
      </div>

      <!-- Step 0: Cover Page -->
      <div class="cover-page" v-if="currentStep === 0" :class="{ 'is-signed-in': isSignedIn }">
        <h1 class="cover-title">RoboFinder</h1>
        <p class="cover-subtitle">on ATTA!</p>
        <p class="cover-description">{{ $t('cover_description') }}</p>
        <p class="cover-subdescription">{{ $t('cover_subdescription') }}</p>
        <button class="start-button" @click="handleStart">
          {{ $t('start_button') }}
        </button>
        <p class="cover-login-note" v-if="!isSignedIn">
          <a href="#" @click.prevent="handleLoginClick">{{ $t('login_link') }}</a>{{ $t('login_required_note') }}
        </p>
      </div>

      <!-- Step 1: Location L1 -->
      <robofinder-question-item
        v-if="currentStep === 1"
        :step="1"
        :question="$t('location_l1_question')"
        :options="categories.location.layer1"
        :selected-value="selections.location_category"
        :is-active="true"
        :can-skip="!!selections.location_category"
        :can-go-back="false"
        :result-count="resultCount"
        :translations="translations"
        :locale="locale"
        @select="handleLocationL1Select"
        @skip="handleLocationL1Skip"
        @email-results="handleEmailResults"
        @close="handleClose"
      />

    <!-- Step 2: Location L2 -->
    <robofinder-question-item
      v-if="currentStep === 2 && availableLocationL2Options.length > 0"
      :step="2"
      :question="$t('location_l2_question')"
      :options="availableLocationL2Options"
      :selected-value="selections.location_subcategory"
      :is-active="true"
      :can-skip="true"
      :can-go-back="true"
      :result-count="resultCount"
      :translations="translations"
      :locale="locale"
      @select="handleLocationL2Select"
      @skip="handleLocationL2Skip"
      @back="handleLocationL2Back"
      @email-results="handleEmailResults"
      @close="handleClose"
    />

    <!-- Step 3: Task L1 -->
    <robofinder-question-item
      v-if="currentStep === 3"
      :step="3"
      :question="$t('task_l1_question')"
      :options="categories.task.layer1"
      :selected-value="selections.task_category"
      :is-active="true"
      :can-skip="true"
      :can-go-back="true"
      :result-count="resultCount"
      :translations="translations"
      :locale="locale"
      @select="handleTaskL1Select"
      @skip="handleTaskL1Skip"
      @back="handleTaskL1Back"
      @email-results="handleEmailResults"
      @close="handleClose"
    />

    <!-- Step 4: Task L2 (conditional) -->
    <robofinder-question-item
      v-if="currentStep === 4 && shouldShowTaskL2"
      :step="3"
      :question="$t('task_l2_question')"
      :options="availableTaskL2Options"
      :selected-value="selections.task_subcategory"
      :is-active="true"
      :can-skip="true"
      :can-go-back="true"
      :result-count="resultCount"
      :translations="translations"
      :locale="locale"
      @select="handleTaskL2Select"
      @skip="handleTaskL2Skip"
      @back="handleTaskL2Back"
      @email-results="handleEmailResults"
      @close="handleClose"
    />

    <!-- Step 5: Purpose L1 -->
    <div v-if="currentStep === 5">
      <robofinder-question-item
        :step="4"
        :question="$t('purpose_l1_question')"
        :options="categories.purpose.layer1"
        :selected-value="selections.purpose_category"
        :is-active="true"
        :can-skip="selections.purpose_category && hasPurposeL2Options"
        :can-go-back="true"
        :show-navigation="true"
        :is-required="!selections.purpose_category || !hasPurposeL2Options"
        :result-count="resultCount"
        :translations="translations"
        :locale="locale"
        @select="handlePurposeL1Select"
        @skip="handlePurposeL1Skip"
        @back="handlePurposeL1Back"
        @email-results="handleEmailResults"
        @close="handleClose"
      />

      <!-- Final step navigation and actions (when no Purpose L2 options and Purpose L1 is selected) -->
      <div class="final-step-container" v-if="selections.purpose_category && !hasPurposeL2Options">
        <!-- Navigation with back button and email link -->
        <div class="final-navigation">
          <button class="back-button" @click="handlePurposeL1Back">
            ←
          </button>
          <a v-if="showEmailResults" href="#" class="email-link" @click.prevent="handleEmailResults">
            {{ $t('email_results') }}
          </a>
          <div class="right-spacer"></div>
        </div>

        <!-- Completion action buttons -->
        <div class="completion-actions">
          <button class="action-button start-again-button" @click="handleStartAgain">
            {{ $t('start_again') }}
          </button>
          <button v-if="resultCount > 0" class="action-button save-result-button" @click="handleSaveResult">
            {{ $t('save_result') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Step 6: Purpose L2 -->
    <div v-if="currentStep === 6 && availablePurposeL2Options.length > 0">
      <robofinder-question-item
        :step="5"
        :question="$t('purpose_l2_question')"
        :options="availablePurposeL2Options"
        :selected-value="selections.purpose_subcategory"
        :is-active="true"
        :can-skip="false"
        :can-go-back="false"
        :show-navigation="false"
        :result-count="resultCount"
        :translations="translations"
        :locale="locale"
        @select="handlePurposeL2Select"
        @skip="handlePurposeL2Skip"
        @back="handlePurposeL2Back"
        @email-results="handleEmailResults"
        @close="handleClose"
      />

      <!-- Final step navigation and actions -->
      <div class="final-step-container">
        <!-- Navigation with back button and email link -->
        <div class="final-navigation">
          <button class="back-button" @click="handlePurposeL2Back">
            <img src="/images/left_switch.png" class="nav-icon" alt="Back">
          </button>
          <a v-if="showEmailResults" href="#" class="email-link" @click.prevent="handleEmailResults">
            {{ $t('email_results') }}
          </a>
          <div class="right-spacer"></div>
        </div>

        <!-- Completion action buttons -->
        <div class="completion-actions">
          <button class="action-button start-again-button" @click="handleStartAgain">
            {{ $t('start_again') }}
          </button>
          <button v-if="resultCount > 0" class="action-button save-result-button" @click="handleSaveResult">
            {{ $t('save_result') }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import RobofinderQuestionItem from './question_item.vue'

export default {
  components: {
    RobofinderQuestionItem,
  },

  props: {
    categories: {
      type: Object,
      required: true
    },
    initialParams: {
      type: Object,
      default: () => ({})
    },
    currentStep: {
      type: Number,
      default: 0
    },
    resultCount: {
      type: Number,
      default: null
    },
    translations: {
      type: Object,
      default: () => ({})
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isSignedIn: {
      type: Boolean,
      default: false
    },
    locale: {
      type: String,
      default: 'en'
    }
  },

  data() {
    return {
      selections: {
        location_category: null,
        location_subcategory: null,
        task_category: null,
        task_subcategory: null,
        purpose_category: null,
        purpose_subcategory: null
      },
      totalSteps: 6
    }
  },

  computed: {
    // Get Location L2 options based on Location L1 selection
    availableLocationL2Options() {
      if (!this.selections.location_category) return [];

      const selectedL1 = this.categories.location.layer1.find(
        item => item.id === parseInt(this.selections.location_category)
      );

      if (!selectedL1 || !selectedL1.layer2_ids) return [];

      return this.categories.location.layer2.filter(
        item => selectedL1.layer2_ids.includes(item.id)
      );
    },

    // Check if Task L2 should be shown based on Task L1 selection
    shouldShowTaskL2() {
      if (!this.selections.task_category) return false;

      const selectedTaskL1 = this.categories.task.layer1.find(
        item => item.id === parseInt(this.selections.task_category)
      );

      return selectedTaskL1 && selectedTaskL1.show_subcategories;
    },

    // Get Task L2 options based on Location L1 selection
    availableTaskL2Options() {
      if (!this.selections.location_category) return [];

      const selectedLocationL1 = this.categories.location.layer1.find(
        item => item.id === parseInt(this.selections.location_category)
      );

      if (!selectedLocationL1 || !selectedLocationL1.task_l2_ids) return [];

      return this.categories.task.layer2.filter(
        item => selectedLocationL1.task_l2_ids.includes(item.id)
      );
    },

    // Get Purpose L2 options based on Purpose L1 selection
    availablePurposeL2Options() {
      if (!this.selections.purpose_category) return [];

      const selectedPurposeL1 = this.categories.purpose.layer1.find(
        item => item.id === parseInt(this.selections.purpose_category)
      );

      if (!selectedPurposeL1 || !selectedPurposeL1.layer2_ids) return [];

      return this.categories.purpose.layer2.filter(
        item => selectedPurposeL1.layer2_ids.includes(item.id)
      );
    },

    // Check if Purpose L2 options exist for the selected Purpose L1
    hasPurposeL2Options() {
      return this.availablePurposeL2Options.length > 0;
    },

    // Check if Email results link should be shown (1-20 results)
    showEmailResults() {
      return this.resultCount !== null && this.resultCount >= 1 && this.resultCount <= 20;
    },

    // Get the label for result count (singular/plural for English, 件 for Japanese)
    resultCountLabel() {
      const resultsText = this.$t('results');
      // 日本語の場合は「件」なのでそのまま返す
      if (resultsText === '件') {
        return resultsText;
      }
      // 英語の場合は1件のときに単数形にする
      return this.resultCount === 1 ? 'result' : 'results';
    }
  },

  methods: {
    handleStart() {
      this.advanceToNextStep(1);
    },

    handleLocationL1Select(value) {
      this.selections.location_category = value;
      // Reset dependent selections
      this.selections.location_subcategory = null;
      this.selections.task_subcategory = null;
      this.emitChange();
      // Don't advance - user will click → to proceed
    },

    handleLocationL1Skip() {
      // → button clicked - advance to next step
      if (this.availableLocationL2Options.length > 0) {
        this.advanceToNextStep(2);
      } else {
        this.advanceToNextStep(3);
      }
    },

    handleLocationL2Select(value) {
      this.selections.location_subcategory = value;
      this.emitChange();
      // Don't advance - user will click → to proceed
    },

    handleLocationL2Skip() {
      // → button clicked - advance to Task L1
      // If not answered, clear the selection
      if (!this.selections.location_subcategory) {
        this.selections.location_subcategory = null;
        this.emitChange();
      }
      this.advanceToNextStep(3);
    },

    handleTaskL1Select(value) {
      this.selections.task_category = value;
      // Reset Task L2 if needed
      this.selections.task_subcategory = null;
      this.emitChange();
      // Don't advance - user will click → to proceed
    },

    handleTaskL1Skip() {
      // → button clicked
      if (this.selections.task_category) {
        // Task L1 answered - advance to Task L2 if should show, otherwise go to Purpose L1
        if (this.shouldShowTaskL2) {
          this.advanceToNextStep(4);
        } else {
          this.advanceToNextStep(5);
        }
      } else {
        // Skip Task L1 - clear and advance to Purpose L1
        this.selections.task_category = null;
        this.selections.task_subcategory = null;
        this.emitChange();
        this.advanceToNextStep(5);
      }
    },

    handleTaskL2Select(value) {
      this.selections.task_subcategory = value;
      this.emitChange();
      // Don't advance - user will click → to proceed
    },

    handleTaskL2Skip() {
      // → button clicked - advance to Purpose L1
      // If not answered, clear the selection
      if (!this.selections.task_subcategory) {
        this.selections.task_subcategory = null;
        this.emitChange();
      }
      this.advanceToNextStep(5);
    },

    handlePurposeL1Select(value) {
      this.selections.purpose_category = value;
      // Reset Purpose L2
      this.selections.purpose_subcategory = null;
      this.emitChange();
      // Don't advance - if Purpose L2 exists, user will click → to proceed
      // If this is final step, completion buttons are already visible
    },

    handlePurposeL1Skip() {
      // → button clicked on Purpose L1
      // If Purpose L2 options exist for the selected Purpose L1, advance to Purpose L2
      // Otherwise, this acts as "skip" - stay on Step 5 but show completion buttons
      if (this.selections.purpose_category && this.hasPurposeL2Options) {
        this.advanceToNextStep(6);
      }
      // If no Purpose L2 options, completion buttons are already visible via final-step-container
      // If Purpose L1 not selected, do nothing (require selection to proceed)
    },

    handlePurposeL2Select(value) {
      this.selections.purpose_subcategory = value;
      this.emitChange();
      // Don't advance - completion buttons are already visible
    },

    handlePurposeL2Skip() {
      // This handler is not used as →/skip button is not shown on Purpose L2
      // Completion buttons are always visible on this final step
    },

    // Back handlers - go to previous step
    handleLocationL2Back() {
      this.selections.location_subcategory = null;
      this.emitChange();
      this.advanceToNextStep(1);
    },

    handleTaskL1Back() {
      this.selections.task_category = null;
      this.selections.task_subcategory = null;
      this.emitChange();
      // Go back to Location L2 if it exists, otherwise Location L1
      if (this.availableLocationL2Options.length > 0) {
        this.advanceToNextStep(2);
      } else {
        this.advanceToNextStep(1);
      }
    },

    handleTaskL2Back() {
      this.selections.task_subcategory = null;
      this.emitChange();
      this.advanceToNextStep(3);
    },

    handlePurposeL1Back() {
      this.selections.purpose_category = null;
      this.selections.purpose_subcategory = null;
      this.emitChange();
      // Go back to Task L2 if it should show, otherwise Task L1
      if (this.shouldShowTaskL2) {
        this.advanceToNextStep(4);
      } else {
        this.advanceToNextStep(3);
      }
    },

    handlePurposeL2Back() {
      this.selections.purpose_subcategory = null;
      this.emitChange();
      this.advanceToNextStep(5);
    },

    /**
     * バツ印タップ時の処理
     * 途中離脱確認モーダルを表示するため、親コンポーネントにイベントを発火
     * 関連: robofinder_app.vue の handleExitRequest
     */
    handleClose() {
      this.$emit('close-request');
    },

    /**
     * 「Start Again」ボタンタップ時の処理
     * Step 1に戻る（選択をリセットして最初の質問へ）
     */
    handleStartAgain() {
      // 選択をリセット
      this.selections = {
        location_category: null,
        location_subcategory: null,
        task_category: null,
        task_subcategory: null,
        purpose_category: null,
        purpose_subcategory: null
      };
      this.emitChange();
      // Step 1に戻る
      this.advanceToNextStep(1);
    },

    handleSaveResult() {
      // 親コンポーネントにsave-resultイベントを発火
      // 親側でログイン状態を確認し、APIを呼び出す
      console.log('questionnaire handleSaveResult called, emitting save-result with:', this.selections);
      this.$emit('save-result', this.selections);
    },

    handleEmailResults() {
      // 親コンポーネントにemail-resultsイベントを発火
      // 親側でログイン状態を確認し、APIを呼び出す
      console.log('questionnaire handleEmailResults called, emitting email-results with:', this.selections);
      this.$emit('email-results', this.selections);
    },

    handleLoginClick() {
      if (this.isMobile) {
        // モバイル: ログインページへ遷移
        window.location.href = '/user/login';
      } else {
        // PC: モーダルを開く
        const signinModal = document.querySelector('.modal-signin');
        if (signinModal) {
          signinModal.classList.remove('hidden');
        }
      }
    },

    advanceToNextStep(nextStep) {
      this.$emit('step-changed', nextStep);
    },

    emitChange() {
      this.$emit('selection-changed', this.selections);
    },

    $t(key) {
      // Use translations passed from Rails via props
      return this.translations?.questionnaire?.[key] || key;
    }
  },

  mounted() {
    // Initialize selections from initial params
    this.selections = {
      location_category: this.initialParams.location_category || null,
      location_subcategory: this.initialParams.location_subcategory || null,
      task_category: this.initialParams.task_category || null,
      task_subcategory: this.initialParams.task_subcategory || null,
      purpose_category: this.initialParams.purpose_category || null,
      purpose_subcategory: this.initialParams.purpose_subcategory || null
    };
  }
}
</script>

<style lang="scss" scoped>
.robofinder-questionnaire {
  display: flex;
  justify-content: center;

  .questionnaire-card {
    // デフォルト（Step 1以降）のスタイル
    width: 360px;
    max-width: 100%;
    height: auto;
    padding: 24px;
    border: 1px solid #707070;
    border-radius: 0;
    background-color: #fff;
    box-shadow: none;

    // カバーページ用スタイル
    &.is-cover {
      border: 1px solid #707070;
      border-radius: 0;
      background-color: #C8C8C8;
      box-shadow: none;
    }

    .cover-page {
      text-align: center;
      padding: 16px 12px;

      .cover-title {
        font-size: 32px;
        font-weight: bold;
        color: #333;
        margin: 0;
      }

      .cover-subtitle {
        font-size: 14px;
        font-weight: bold;
        color: #333;
        margin: 0 0 16px 0;
      }

      .cover-description {
        font-size: 20px;
        font-weight: bold;
        color: #333;
        margin: 0 0 6px 0;
      }

      .cover-subdescription {
        font-size: 14px;
        color: #555;
        margin: 0 0 16px 0;
        line-height: 1.4;
      }

      .start-button {
        width: 140px;
        padding: 10px 20px;
        border: none;
        border-radius: 0;
        background-color: #F5A623;
        color: #000;
        font-size: 20px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.2s ease;
        margin: 0 0 12px 0;

        &:hover {
          background-color: #e6951a;
        }
      }

      .cover-login-note {
        font-size: 12px;
        color: #555;
        margin: 0;

        a {
          color: #555;
          text-decoration: underline;
        }
      }

      &.is-signed-in {
        .start-button {
          margin: 0;
        }
      }
    }

    .results-header {
      text-align: center;
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px solid #e0e0e0;
      position: relative;

      .results-count {
        font-size: 28px;
        font-weight: bold;
        color: #333;
        margin: 0 0 16px 0;
      }

      .progress-dots {
        display: flex;
        justify-content: center;
        gap: 8px;

        .progress-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #d0d0d0;
          transition: background-color 0.3s ease;

          &.is-active {
            background-color: #4a4a4a;
          }
        }
      }

      .header-close-button {
        position: absolute;
        top: 0;
        right: 0;
        width: 32px;
        height: 32px;
        border: none;
        border-radius: 50%;
        background-color: transparent;
        color: #666;
        font-size: 32px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;

        &:hover {
          background-color: transparent;
          color: #333;
        }
      }
    }

    .final-step-container {
      margin-top: 16px;

      .final-navigation {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 20px;

        .back-button {
          width: 48px;
          height: 48px;
          border: none;
          border-radius: 0;
          background-color: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
          padding: 0;

          .nav-icon {
            width: 100%;
            height: 100%;
            object-fit: contain;
            content: url('/images/left_switch.png');
          }

          &:hover .nav-icon {
            content: url('/images/left_switch_hover.png');
          }
        }

        .email-link {
          color: #4a90e2;
          text-decoration: underline;
          font-size: 15px;
          cursor: pointer;

          &:hover {
            color: #357abd;
          }
        }

        // 右側のバランス用スペーサー
        .right-spacer {
          width: 48px;
          flex-shrink: 0;
        }
      }
    }

    .question-navigation {
      display: flex;
      justify-content: space-between;
      margin-top: 16px;

      .back-button {
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 0;
        background-color: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        margin-right: auto;
        padding: 0;

        .nav-icon {
          width: 100%;
          height: 100%;
          object-fit: contain;
          content: url('/images/left_switch.png');
        }

        &:hover .nav-icon {
          content: url('/images/left_switch_hover.png');
        }
      }
    }

    .completion-actions {
      display: flex;
      flex-direction: row;
      gap: 12px;

      .action-button {
        flex: 1;
        padding: 14px 20px;
        border: 2px solid #4a4a4a;
        border-radius: 0;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;

        &.start-again-button {
          background-color: #fff;
          color: #4a4a4a;

          &:hover {
            background-color: #f5f5f5;
          }
        }

        &.save-result-button {
          background-color: #ff9933;
          color: #000;
          border-color: #ff9933;

          &:hover {
            background-color: #ff8800;
            border-color: #ff8800;
          }
        }
      }
    }
  }
}
</style>
